<template>
  <div
    class="field-popup"
    :class="{ 'field-popup--no-buttons': !showPopupButtons }"
    :data-id="id"
    :id="`field-popup-${id}`"
    v-if="id">
    <div class="field-popup__action-message" v-if="!$store.state.isMobile">
      <icon name="exclamation"></icon>
      <span>{{ $t('fieldPopup.tabOrClickToContinue') }}</span>
    </div>
    <h2 class="field-popup__header">
      <span class="field-popup__header__text" v-if="currentField.type !== 'dropdown'">{{ currentField.title === $t('fieldPopup.untitled') ? $t('fieldPopup.addYourField', { type: $t(`fieldPopup.type.${currentField.type}`) }) : currentField.title }}</span>
      <span class="field-popup__header__text" v-else>{{ $t('fieldPopup.selectAnOption') }}</span>
      <span v-if="currentField.required === 'true'" class="field-popup__header__label">
        <icon name="asterisk" height="10" width="10"></icon>
        <span>{{ $t('fieldPopup.requiredField') }}</span>
      </span>
      <a class="field-popup__close" @click="closeFieldPopup">
        <icon name="close" width="16" height="16"></icon>
      </a>
    </h2>

    <!-- Signature -->
    <div class="field-popup__content" v-if="currentField.type == 'signature'">
      <tabs type="signature">
        <tab :name="$t('fieldPopup.signature.draw')" property="drawn" id="1" iconName="pencil" v-if="config.company_signature_format_drawn === '1'">
          <signature-box
            :currentField="currentField"
            type="draw"
            :activateConfirmButton="activateConfirmButton"
            :deactivateConfirmButton="deactivateConfirmButton"
            :key="`sig-box-draw-${id}`"></signature-box>
        </tab>
        <tab :name="$t('fieldPopup.signature.type')" property="typed" id="2" iconName="font" :hasInput="true" v-if="config.company_signature_format_typed === '1'">
          <signature-box
            :currentField="currentField"
            type="type"
            :activateConfirmButton="activateConfirmButton"
            :deactivateConfirmButton="deactivateConfirmButton"
            :key="`sig-box-type-${id}`"></signature-box>
        </tab>
        <tab :name="$t('fieldPopup.signature.upload')" property="uploaded" id="3" iconName="cloud-upload" v-if="config.company_signature_format_upload === '1'">
          <fine-uploader
            :key="`signature-uploader-${id}`"
            :activate-confirm-button="activateConfirmButton"
            :deactivate-confirm-button="deactivateConfirmButton"
            :field-id="id"
            :options="signatureUploadOpts"
            v-if="!uploaderHidden"
            type="imageUpload"></fine-uploader>
        </tab>
      </tabs>
    </div>

    <!-- Text -->
    <div class="field-popup__content" v-if="currentField.type == 'text'">
      <input class="field-popup__field-text" :name="currentField.title !== 'Untitled' ? currentField.title : currentField.id" type="text" data-cy="submit-text-input" @input="updateFieldText" ref='input' />
    </div>

    <!-- Textarea -->
    <div class="field-popup__content" v-if="currentField.type == 'textarea'">
      <textarea class="field-popup__field-textarea" @input="updateFieldText" ref='input'></textarea>
    </div>

    <!-- Dropdown -->
    <div class="field-popup__content field-popup__content--dropdown" v-if="currentField.type == 'dropdown'">
      <div
        v-for="choice in currentField.dropdown_choices"
        @click.self="selectDropdownChoice"
        :key="choice"
        :class="{ selected: currentField.content === choice }">{{ choice }}</div>
    </div>

    <!-- Date -->
    <div class="field-popup__content" v-if="currentField.type == 'date'">
      <div class="grid">
        <div class="grid__item grid__item--6">
          <datepicker
            v-model="pickedDate"
            :inline="true"
            :field-id="id"
            :language="$i18n.locale"></datepicker>
        </div>
        <div class="grid__item grid__item--6">
          <h1 class="field-popup__date-text" v-if="pickedDate">{{ pickedDate }}</h1>
        </div>
      </div>
    </div>

    <!-- Upload -->
    <div class="field-popup__content" v-if="currentField.type == 'upload'">
      <fine-uploader
        :activate-confirm-button="activateConfirmButton"
        :deactivate-confirm-button="deactivateConfirmButton"
        :field-id="id"
        :options="documentUploadOpts"
        :key="`file-uploader-${id}`"
        v-if="!uploaderHidden"
        type="documentUpload"></fine-uploader>
    </div>

    <div class="field-popup__buttons" v-if="showPopupButtons">
      <app-button
        v-if="currentField.type === 'signature' || currentField.type == 'upload' || currentField.type === 'date'"
        :text="$t('functional.confirm')"
        iconName="check"
        :action="confirm"
        data-cy="confirm"
        :isActive="isButtonActive"
        classList="button--cta float-right"></app-button>
      <app-button
        v-else
        :text="$store.getters.footerNextButtonText()"
        :action="nextAction"
        data-cy="field-next"
        classList="button--cta float-right"></app-button>
    </div>
  </div>
</template>

<script>

import SignatureBox from '@/components/SignatureBox'
import FineUploader from '@/components/FineUploader'
import Tabs from '@/components/Tabs'
import Tab from '@/components/Tab'
import AppButton from '@/components/AppButton'
import Datepicker from '@/components/DatePicker'
import nextActionUtil from '@/utils/next-action'

export default {
  name: 'field-popup',
  props: [
    'activeField'
  ],
  components: {
    SignatureBox,
    FineUploader,
    Tabs,
    Tab,
    Datepicker,
    AppButton
  },
  data () {
    return {
      uploaderHidden: false,
      config: this.$store.state.config,
      isButtonActive: false,
      pickedDate: null,
      documentUploadOpts: {
        options: {
          request: {
            endpoint: `${process.env.VUE_APP_API_URL}/ajax/upload-field.php`
          },
          deleteFile: {
            endpoint: `${process.env.VUE_APP_API_URL}/ajax/upload-field.php`,
            enabled: true,
            method: 'POST'
          },
          validation: {
            allowedExtensions: ['jpeg', 'jpg', 'png', 'pdf', 'doc', 'docx', 'bmp'],
            acceptFiles: 'image/jpeg, image/png, application/pdf, application/x-pdf, application/msword, application/doc, application/vnd.msword, application/vnd.ms-word, application/winword, application/word, application/x-msw6, application/x-msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/bmp',
            sizeLimit: 5000000
          },
          multiple: true
        }
      },
      signatureUploadOpts: {
        options: {
          request: {
            endpoint: `${process.env.VUE_APP_API_URL}/ajax/upload-signature.php`
          },
          multiple: false,
          validation: {
            allowedExtensions: ['jpeg', 'jpg', 'png'],
            acceptFiles: 'image/jpeg, image/png',
            sizeLimit: 5000000,
            itemLimit: 1
          }
        }
      }
    }
  },
  created () {
    this.$bus.$on('confirmField', () => {
      if (parseInt(this.$store.state.activeFieldId, 10) === parseInt(this.id, 10) && this.isButtonActive) {
        this.confirm()
      }
    })
    this.$bus.$on('calendarSelected', () => {
      this.isButtonActive = true
    })
    this.$bus.$on('disableButton', () => {
      this.isButtonActive = false
    })
    this.$bus.$on('updateSignature', (value, property) => {
      this.updateFieldValue(value, property)
    })

    this.$bus.$on('force-reload-component', () => {
      this.forceReloadComponent()
    })
  },
  updated () {
    const fieldType = this.currentField.type
    if (fieldType === 'text' || fieldType === 'textarea') {
      this.$refs.input.focus()
      this.$refs.input.value = this.currentField.content
      if (this.$refs.input.value.trim() === '') {
        this.isButtonActive = false
      }
    } else if (fieldType === 'date') {
      if (!this.currentField.content) this.isButtonActive = false
    } else if (fieldType === 'upload') {
      if (!this.currentField.upload || !this.currentField.upload.length) {
        this.isButtonActive = false
      } else {
        this.isButtonActive = true
      }
    }
  },
  computed: {
    id () {
      return this.$store.state.activeFieldId
    },
    showPopupButtons () { // TODO This is not the nicest workaround, refactor
      if (this.currentField.type === 'text' || this.currentField.type === 'textarea') return false
      return true
    },
    currentField () {
      return this.$store.getters.getField(this.id, false)
    }
  },
  methods: {
    forceReloadComponent () { // TODO This is a horrible hack because the FineUploader component stops refreshing completely when the uploadComplete event is called
      this.uploaderHidden = true
      setTimeout(() => {
        this.uploaderHidden = false
      }, 1)
    },
    updateFieldText (event) {
      const fingerprint = this.$store.getters.currentDocumentFingerPrint
      const fieldText = event.target.value

      this.$store.dispatch('setCurrentFieldVal', {
        val: fieldText,
        id: this.currentField.id,
        property: 'content'
      })

      if ((!event.target.value || (event.target.value && event.target.value.trim().length === 0)) && this.currentField.required === 'true') { // eslint-disable-line no-mixed-operators
        this.$store.commit('removeDoneField', { field: this.currentField, fingerprint })
        this.isButtonActive = false
      } else {
        this.isButtonActive = true
        this.$store.dispatch('addDoneField', { field: this.currentField })
      }
    },
    updateFieldValue (value, property) {
      const fingerprint = this.$store.getters.currentDocumentFingerPrint

      this.$store.dispatch('setCurrentFieldVal', {
        val: value,
        id: this.currentField.id,
        property: property || 'content'
      })

      if (this.currentField.type === 'signature') { // Clear other signature types
        if (this.currentField.drawn_json && property !== 'drawn_json') {
          this.$store.dispatch('setCurrentFieldVal', {
            val: null,
            id: this.currentField.id,
            property: 'drawn_json'
          })
        }

        if (this.currentField.content && !(!property || property === 'content')) {
          this.$store.dispatch('setCurrentFieldVal', {
            val: null,
            id: this.currentField.id,
            property: 'content'
          })
        }

        if (this.currentField.upload && property !== 'upload') {
          this.$store.dispatch('setCurrentFieldVal', {
            val: null,
            id: this.currentField.id,
            property: 'upload'
          })
        }
      }

      const valueGiven = () => {
        if (!value) return false
        if (Array.isArray(value)) return true
        if (value.trim().length > 0) return true
        return false
      }

      if ((!property || property === 'content' || property === 'drawn_json') && !(valueGiven())) {
        this.$store.commit('removeDoneField', { field: this.currentField, fingerprint })
        this.isButtonActive = false
      } else {
        this.isButtonActive = true
        this.$store.dispatch('addDoneField', { field: this.currentField })
      }
    },
    selectDropdownChoice (event) {
      const value = event.target.innerText

      this.$store.dispatch('setCurrentFieldVal', {
        id: this.currentField.id,
        val: value,
        property: 'content'
      })

      this.$store.dispatch('addDoneField', { field: this.currentField })
    },
    closeFieldPopup () {
      this.$store.commit('setActiveField', { activeFieldId: null })
    },
    nextAction () {
      nextActionUtil()
    },
    // bottomStyle () {
    //   const bottom = this.currentField.type === 'date' ? this.$el.clientHeight + 382 : this.$el.clientHeight + 100
    //   if (!this.isActive) return `-${bottom}px`
    //   return '0px'
    // },
    confirm () { // Stop deleting uploaded files being treated as having been completed.
      if (this.currentField.type === 'signature') this.$bus.$emit('confirmSignatureField', this.id)

      if (this.currentField.type !== 'upload') {
        this.$store.dispatch('addDoneField', { field: this.currentField })
      }

      this.$store.commit('setActiveField', { activeFieldId: null })
      this.nextAction()
    },
    activateConfirmButton () {
      this.isButtonActive = true
    },
    deactivateConfirmButton () {
      this.isButtonActive = false
    }
  }
}
</script>

<style lang="sass" scoped>
  @import '@/styles/component-utils.sass'
  $color-popup-shadow: $color-grey
  .field-popup
    background-color: #fff
    position: fixed
    bottom: 0%
    right: 0
    width: 75%
    z-index: 10
    transition: bottom .25s ease-out
    box-shadow: 0px -20px 30px -30px $color-popup-shadow
    cursor: default
    z-index: 999
    opacity: 0
    transition: all .25s ease
    color: #555
    opacity: 1
    &--no-buttons
      margin-bottom: -15px
      position: initial
      width: initial
    &__action-message
      position: absolute
      top: -32px
      background: #000
      color: #fff
      padding: 5px 10px
      border-radius: 5px
      transform: translate3d(-50%, 0, 0)
      left: 50%
      font-size: 11px
      font-weight: bold
      text-transform: uppercase
      svg
        height: 12px
        width: 5px
        float: left
    &__header
      padding: $spacing-base
      border-bottom: 1px solid $color-border
      margin: 0
      position: relative
      @include clearfix
      &__text
        float: left
      &__label
        float: left
        font-size: 9px
        padding: $spacing-xs
        background: $color-cancel
        color: #fff
        border: 1px solid $color-cancel-dark
        border-radius: 3px
        margin-left: $spacing-small
        margin-top: $spacing-xs
        text-transform: uppercase
        svg
          margin: 0
          float: left
        span
          float: left
          margin-left: $spacing-xs
    &__content
      padding: $spacing-base
      &--dropdown
        padding: 0
        background: #FAFBFB
        border-bottom: 1px solid #ecf0f1
        margin-bottom: $spacing-base
        box-shadow: inset 0px 0px 7px 0 #eee
        max-height: 165px
        overflow: auto
        > div
          padding: $spacing-base
          font-size: 20px
          &:hover,
          &.selected,
            cursor: pointer
          &:hover
            background: $signable-secondary-1-shade-3
          &.selected
            color: #fff
            background: $signable-secondary-1
    &__buttons
      padding: 0 $spacing-base $spacing-base
      @include clearfix
    &__field-text,
    &__field-textarea,
    &__field-dropdown
      width: 100%
      padding: $spacing-base
      border: 1px solid $color-border-dark
      font-size: $font-size-heading
    &__field-textarea
      height: 250px
    &__field-dropdown
      padding: $spacing-base
      -webkit-appearance: none
      color: inherit
      font-family: inherit
    &__close
      position: absolute
      right: $spacing-base
      top: 12px
      padding: 7px
      background: $color-cancel
      border: 2px solid $color-cancel-dark
      color: #fff
      border-radius: 30px
      line-height: 0
      cursor: pointer
      svg
        margin-right: 0
    &__date-text
      position: absolute
      right: calc(50% - 50%)
      top: 50%
      -ms-top: 0
      transform: translate3d(calc(-50% - (50% - 25%)), -50%, 0)
      -ms-transform: translate3d(-100%, -100%, 0px)
      margin: 0
      padding: $spacing-base
      background-color: $color-blue
      color: #fff
      border-radius: 3px
  // Responsive
  @media screen and (max-width: $screen-size-large)
    .field-popup
      width: 100%
      &__date-text
        position: initial
        right: 0
        transform: none
        text-align: center
  @media screen and (max-width: $screen-size-small)
    .field-popup
      &__date-text
        margin-top: $spacing-small
</style>
